import React, { useState, useEffect } from "react";
import { ScrollText, Search, Download, Loader2 } from "lucide-react";
import { api } from "../../services/authService";

interface AuditLogRecord {
  id: string;
  event_type: string;
  tenant_schema: string;
  ip_address: string;
  user_agent: string;
  details: Record<string, any>;
  timestamp: string;
  user_email: string;
}

export const AuditLogPage: React.FC = () => {
  const [logs, setLogs] = useState<AuditLogRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [category, setCat] = useState("All");

  useEffect(() => {
    const fetchAuditLogs = async () => {
      try {
        const res = await api.get("/auth/audit-logs/");
        setLogs(Array.isArray(res.data) ? res.data : (res.data.results || []));
      } catch (err) {
        setLogs([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchAuditLogs();
  }, []);

  const filtered = logs.filter((l) => {
    const q = query.toLowerCase();
    const matchQ =
      !q ||
      (l.user_email && l.user_email.toLowerCase().includes(q)) ||
      (l.event_type && l.event_type.toLowerCase().includes(q)) ||
      (l.ip_address && l.ip_address.includes(q));
    const matchCat = category === "All" || l.event_type.toLowerCase().includes(category.toLowerCase());
    return matchQ && matchCat;
  });

  return (
    <div className="space-y-4 text-xs font-sans">
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-slate-900/80 border border-slate-800 rounded-xl">
        <div className="flex items-center gap-2">
          <ScrollText className="w-5 h-5 text-indigo-400" />
          <div>
            <h2 className="text-sm font-bold text-slate-100">Security & Administrative Audit Trail</h2>
            <p className="text-[10px] text-slate-500">Tenant-isolated, tamper-evident log of security events and user actions.</p>
          </div>
        </div>
        <button
          onClick={() => {
            const csv = ["ID,Event,Email,IP,Timestamp", ...logs.map(l => `"${l.id}","${l.event_type}","${l.user_email}","${l.ip_address}","${l.timestamp}"`)].join("\n");
            const blob = new Blob([csv], { type: "text/csv" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "audit_log.csv";
            a.click();
          }}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg transition-colors text-[11px]"
        >
          <Download className="w-3.5 h-3.5" />
          Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 p-3.5 bg-slate-900/80 border border-slate-800 rounded-xl">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
          <input
            type="text"
            placeholder="Search email, event, IP…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full bg-slate-950 border border-slate-700 rounded-lg pl-8 pr-3 py-2 text-slate-200 text-[11px] placeholder-slate-500 focus:outline-none focus:border-indigo-600"
          />
        </div>
        <select
          value={category}
          onChange={(e) => setCat(e.target.value)}
          className="bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-slate-200 text-[11px] focus:outline-none focus:border-indigo-600"
        >
          <option value="All">All Events</option>
          <option value="login">Login Events</option>
          <option value="password">Password Events</option>
          <option value="registration">Registration Events</option>
        </select>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-3 gap-2">
        {[
          { label: "Total Recorded Events", val: logs.length, color: "text-slate-100" },
          { label: "Login Events", val: logs.filter((l) => l.event_type.includes("login")).length, color: "text-indigo-400" },
          { label: "Security Events", val: logs.filter((l) => l.event_type.includes("password")).length, color: "text-amber-400" },
        ].map((s) => (
          <div key={s.label} className="p-3 bg-slate-950 border border-slate-800 rounded-xl text-center">
            <p className={`text-xl font-bold font-mono ${s.color}`}>{s.val}</p>
            <p className="text-[10px] text-slate-500 mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Log Entries */}
      {isLoading ? (
        <div className="p-12 text-center text-slate-500 text-xs flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin text-indigo-400" /> Fetching audit logs...
        </div>
      ) : (
        <div className="space-y-2 font-mono">
          {filtered.map((log) => (
            <div key={log.id} className="p-3.5 border bg-slate-950 border-slate-800 rounded-xl">
              <div className="flex items-center justify-between gap-2 mb-1.5 flex-wrap">
                <div className="flex items-center gap-2 text-[10px] text-slate-500">
                  <code className="text-indigo-400 font-bold">{log.id.slice(0, 8)}</code>
                  <span>|</span>
                  <span>{new Date(log.timestamp).toLocaleString()}</span>
                  <span>|</span>
                  <span className="text-slate-400">IP: {log.ip_address || "N/A"}</span>
                </div>
                <span className="text-[9px] font-bold px-2 py-0.5 rounded bg-indigo-950 text-indigo-300 border border-indigo-800 uppercase">
                  {log.event_type}
                </span>
              </div>
              <div className="flex items-start gap-2 text-[11px] flex-wrap">
                <span className="text-slate-200 font-bold font-sans">{log.user_email}</span>
                <span className="text-slate-400">({log.tenant_schema})</span>
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="text-center py-10 text-slate-500 font-sans">No audit records found for this tenant.</div>
          )}
        </div>
      )}
    </div>
  );
};
